/**
 * React Query hooks and helpers for the five Finance reports under Reports > Finance:
 *
 *   useCashCredit(range, stores, grain)   GET /v1/retail/finance/cash-credit
 *   useTrailing12()                       GET /v1/retail/finance/trailing-12
 *   useSalesTax(range, stores)            GET /v1/retail/finance/sales-tax
 *   useGiftCards(range)                   GET /v1/retail/finance/gift-cards
 *   useGlExports(month)                   GET /v1/retail/finance/gl-exports
 *   downloadFinanceXlsx(path, query, name) the matching /export and /download routes
 *
 * Head office only: the api answers 403 to anyone without the report's own slug, so the
 * pages show the access card before calling anything. The mirrors refresh once a day, so
 * a few minutes of cache is plenty. Same 404/503 "not deployed yet" convention as the
 * other report hooks (see isNotAvailable in useStores).
 *
 * The row types come from `@ampersandbrands/ui`, because the shared screens own the wire
 * shape. What lives here is the query string and URL handling, which is pure and tested.
 */

import { useQuery } from '@tanstack/react-query';
import type {
  CashCreditGrain,
  CashCreditRow,
  CashCreditSummary,
  FinanceDateRange,
  FinanceStoreOption,
  GiftKpis,
  GiftMonthLine,
  GiftPeriod,
  GiftRange,
  GiftSoldMonth,
  GiftSoldStore,
  GiftTotals,
  GlJournalInfo,
  GlPeriod,
  GlSelected,
  SalesTaxRow,
  SalesTaxSummary,
  Trailing12Month,
  Trailing12Row,
  Trailing12Totals,
} from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';

export const FINANCE_API_BASE = `${RETAIL_API_BASE}/finance`;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Today in Denver, the business day the stores trade on, for the date pickers' upper bound. */
export function denverToday(now: Date = new Date()): string {
  return now.toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
}

// ---------------------------------------------------------------------------
// Query strings and URL state
// ---------------------------------------------------------------------------

/** `from` and `to` in the page URL, when both are real dates; otherwise null, meaning the report's default range. */
export function parseRange(sp: URLSearchParams): FinanceDateRange | null {
  const from = sp.get('from');
  const to = sp.get('to');
  if (from && to && DATE_RE.test(from) && DATE_RE.test(to) && from <= to) return { from, to };
  return null;
}

export function writeRange(sp: URLSearchParams, range: FinanceDateRange | null): void {
  sp.delete('from');
  sp.delete('to');
  if (range) {
    sp.set('from', range.from);
    sp.set('to', range.to);
  }
}

export function parseStores(sp: URLSearchParams): string[] {
  return sp.getAll('store_no').filter(Boolean);
}

export function writeStores(sp: URLSearchParams, stores: string[]): void {
  sp.delete('store_no');
  for (const s of stores) sp.append('store_no', s);
}

export function parseGrain(sp: URLSearchParams): CashCreditGrain {
  return sp.get('grain') === 'day' ? 'day' : 'store';
}

export function writeGrain(sp: URLSearchParams, grain: CashCreditGrain): void {
  sp.delete('grain');
  if (grain === 'day') sp.set('grain', 'day');
}

/** Query string for a range report and its export. Store numbers repeat their key. */
export function buildFinanceQs(range: FinanceDateRange | null, stores: string[] = [], extra: Record<string, string> = {}): string {
  const sp = new URLSearchParams();
  if (range) {
    sp.set('from', range.from);
    sp.set('to', range.to);
  }
  for (const s of stores) sp.append('store_no', s);
  for (const [k, v] of Object.entries(extra)) sp.set(k, v);
  return sp.toString();
}

/** The chosen GL fiscal month in the page URL (`fy` and `fm`), or null for the newest complete month. */
export function parseGlMonth(sp: URLSearchParams): { fiscal_year: number; fiscal_month: number } | null {
  const y = Number(sp.get('fy'));
  const m = Number(sp.get('fm'));
  return Number.isInteger(y) && Number.isInteger(m) && y > 0 && m > 0 ? { fiscal_year: y, fiscal_month: m } : null;
}

export function writeGlMonth(sp: URLSearchParams, month: { fiscal_year: number; fiscal_month: number } | null): void {
  sp.delete('fy');
  sp.delete('fm');
  if (month) {
    sp.set('fy', String(month.fiscal_year));
    sp.set('fm', String(month.fiscal_month));
  }
}

// ---------------------------------------------------------------------------
// Cash & Credit
// ---------------------------------------------------------------------------

export type CashCreditResponse = {
  as_of: string | null;
  from: string;
  to: string;
  grain: CashCreditGrain;
  summary: CashCreditSummary;
  rows: CashCreditRow[];
  stores: FinanceStoreOption[];
};

const ZERO_CASH: CashCreditSummary = { cash: 0, credit: 0, total: 0, stores: 0, days: 0 };

/** Tolerant read, so a partial or absent payload degrades to an empty report rather than a crash. */
export function normalizeCashCredit(raw: unknown): CashCreditResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    as_of: str(r.as_of),
    from: str(r.from) ?? '',
    to: str(r.to) ?? '',
    grain: r.grain === 'day' ? 'day' : 'store',
    summary: { ...ZERO_CASH, ...((r.summary ?? {}) as Partial<CashCreditSummary>) },
    rows: arr<CashCreditRow>(r.rows),
    stores: arr<FinanceStoreOption>(r.stores),
  };
}

export function useCashCredit(range: FinanceDateRange | null, stores: string[], grain: CashCreditGrain, enabled: boolean) {
  const query = useQuery<CashCreditResponse, Error>({
    queryKey: ['retail', 'finance', 'cash-credit', range, stores, grain],
    queryFn: async () => {
      const qs = buildFinanceQs(range, stores, grain === 'day' ? { grain: 'day' } : {});
      return normalizeCashCredit(await apiGet<unknown>(qs ? `${FINANCE_API_BASE}/cash-credit?${qs}` : `${FINANCE_API_BASE}/cash-credit`));
    },
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  const d = query.data;
  return {
    asOf: d?.as_of ?? null,
    range: d && d.from ? { from: d.from, to: d.to } : null,
    summary: d?.summary ?? null,
    rows: d?.rows ?? [],
    stores: d?.stores ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Trailing 12
// ---------------------------------------------------------------------------

export type Trailing12Response = {
  as_of: string | null;
  months: Trailing12Month[];
  rows: Trailing12Row[];
  totals: Trailing12Totals | null;
};

export function normalizeTrailing12(raw: unknown): Trailing12Response {
  const r = (raw ?? {}) as Record<string, unknown>;
  const t = (r.totals ?? null) as Partial<Trailing12Totals> | null;
  return {
    as_of: str(r.as_of),
    months: arr<Trailing12Month>(r.months),
    rows: arr<Trailing12Row>(r.rows),
    totals: t ? { cells: arr<number>(t.cells), total: num(t.total) } : null,
  };
}

export function useTrailing12(enabled: boolean) {
  const query = useQuery<Trailing12Response, Error>({
    queryKey: ['retail', 'finance', 'trailing-12'],
    queryFn: async () => normalizeTrailing12(await apiGet<unknown>(`${FINANCE_API_BASE}/trailing-12`)),
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
  const d = query.data;
  return {
    asOf: d?.as_of ?? null,
    months: d?.months ?? [],
    rows: d?.rows ?? [],
    totals: d?.totals ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Sales tax
// ---------------------------------------------------------------------------

export type SalesTaxResponse = {
  as_of: string | null;
  from: string;
  to: string;
  summary: SalesTaxSummary;
  rows: SalesTaxRow[];
  stores: FinanceStoreOption[];
};

const ZERO_TAX: SalesTaxSummary = { tickets: 0, taxable: 0, nontaxable: 0, total_sales: 0, norm_tax: 0, tax: 0 };

export function normalizeSalesTax(raw: unknown): SalesTaxResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    as_of: str(r.as_of),
    from: str(r.from) ?? '',
    to: str(r.to) ?? '',
    summary: { ...ZERO_TAX, ...((r.summary ?? {}) as Partial<SalesTaxSummary>) },
    rows: arr<SalesTaxRow>(r.rows),
    stores: arr<FinanceStoreOption>(r.stores),
  };
}

export function useSalesTax(range: FinanceDateRange | null, stores: string[], enabled: boolean) {
  const query = useQuery<SalesTaxResponse, Error>({
    queryKey: ['retail', 'finance', 'sales-tax', range, stores],
    queryFn: async () => {
      const qs = buildFinanceQs(range, stores);
      return normalizeSalesTax(await apiGet<unknown>(qs ? `${FINANCE_API_BASE}/sales-tax?${qs}` : `${FINANCE_API_BASE}/sales-tax`));
    },
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  const d = query.data;
  return {
    asOf: d?.as_of ?? null,
    range: d && d.from ? { from: d.from, to: d.to } : null,
    summary: d?.summary ?? null,
    rows: d?.rows ?? [],
    stores: d?.stores ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Gift cards
// ---------------------------------------------------------------------------

export type GiftCardsResponse = {
  as_of: string | null;
  breakage_factor: string;
  cutoff_date: string;
  periods: GiftPeriod[];
  range: GiftRange | null;
  kpis: GiftKpis | null;
  months: GiftMonthLine[];
  totals: GiftTotals | null;
  by_month_sold: GiftSoldMonth[];
  by_store: GiftSoldStore[];
};

export function normalizeGiftCards(raw: unknown): GiftCardsResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  const range = (r.range ?? null) as Partial<GiftRange> | null;
  return {
    as_of: str(r.as_of),
    breakage_factor: str(r.breakage_factor) ?? '0.279950',
    cutoff_date: str(r.cutoff_date) ?? '2020-03-04',
    periods: arr<GiftPeriod>(r.periods),
    range: range && range.from && range.to ? { from: range.from, to: range.to } : null,
    kpis: (r.kpis ?? null) as GiftKpis | null,
    months: arr<GiftMonthLine>(r.months),
    totals: (r.totals ?? null) as GiftTotals | null,
    by_month_sold: arr<GiftSoldMonth>(r.by_month_sold),
    by_store: arr<GiftSoldStore>(r.by_store),
  };
}

export function useGiftCards(range: GiftRange | null, enabled: boolean) {
  const query = useQuery<GiftCardsResponse, Error>({
    queryKey: ['retail', 'finance', 'gift-cards', range],
    queryFn: async () => {
      const qs = buildFinanceQs(range);
      return normalizeGiftCards(await apiGet<unknown>(qs ? `${FINANCE_API_BASE}/gift-cards?${qs}` : `${FINANCE_API_BASE}/gift-cards`));
    },
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  const d = query.data;
  return {
    data: d ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// GL exports
// ---------------------------------------------------------------------------

export type GlExportsResponse = {
  as_of: string | null;
  periods: GlPeriod[];
  selected: GlSelected | null;
  journals: GlJournalInfo[];
};

export function normalizeGlExports(raw: unknown): GlExportsResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    as_of: str(r.as_of),
    periods: arr<GlPeriod>(r.periods),
    selected: (r.selected ?? null) as GlSelected | null,
    journals: arr<GlJournalInfo>(r.journals),
  };
}

export function useGlExports(month: { fiscal_year: number; fiscal_month: number } | null, enabled: boolean) {
  const query = useQuery<GlExportsResponse, Error>({
    queryKey: ['retail', 'finance', 'gl-exports', month],
    queryFn: async () => {
      const qs = month ? `fiscal_year=${month.fiscal_year}&fiscal_month=${month.fiscal_month}` : '';
      return normalizeGlExports(await apiGet<unknown>(qs ? `${FINANCE_API_BASE}/gl-exports?${qs}` : `${FINANCE_API_BASE}/gl-exports`));
    },
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  const d = query.data;
  return {
    asOf: d?.as_of ?? null,
    periods: d?.periods ?? [],
    selected: d?.selected ?? null,
    journals: d?.journals ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Downloads
// ---------------------------------------------------------------------------

/** Browser download of an Excel route (`cash-credit/export`, `gl-exports/download`, and so on). */
export async function downloadFinanceXlsx(path: string, qs: string, fallbackName: string): Promise<void> {
  const res = await fetch(`${FINANCE_API_BASE}/${path}${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Download failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
